import React, { useEffect, useRef, useState } from 'react';
import { CloudUpload, X } from 'lucide-react';

export interface ReHubUploadFile {
  id: string;
  file: File;
  error?: string;
}

export function ReHubUploadPanel({
  open,
  title = 'Upload photos',
  description,
  accept,
  multiple = true,
  files,
  onFiles,
  onRemove,
  onClose,
  onSubmit,
  submitLabel = 'Continue',
  submitting = false,
  children,
}: {
  open: boolean;
  title?: string;
  description?: string;
  accept?: string;
  multiple?: boolean;
  files: ReHubUploadFile[];
  onFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  submitting?: boolean;
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const previews = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const current = new Set(files.map((item) => item.id));
    previews.current.forEach((url, id) => {
      if (!current.has(id)) {
        URL.revokeObjectURL(url);
        previews.current.delete(id);
      }
    });
  }, [files]);

  useEffect(() => () => {
    previews.current.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const previewFor = (item: ReHubUploadFile) => {
    if (item.error || !item.file.type.startsWith('image/')) return null;
    const existing = previews.current.get(item.id);
    if (existing) return existing;
    const url = URL.createObjectURL(item.file);
    previews.current.set(item.id, url);
    return url;
  };

  const take = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onFiles(Array.from(list));
  };

  return (
    <div className="rehub-dash fixed inset-0 z-[70] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="rehub-upload-title">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-[#0e1420] shadow-2xl sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div>
            <h2 id="rehub-upload-title" className="text-lg font-semibold">{title}</h2>
            {description && <p className="mt-1 text-sm text-slate-400">{description}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close upload" className="rounded-lg p-1.5 text-slate-400 hover:bg-white/5 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div
            onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(event) => { event.preventDefault(); setDragOver(false); take(event.dataTransfer.files); }}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed px-4 py-8 text-center ${dragOver ? 'border-orange-400 bg-orange-500/10' : 'border-white/15 bg-white/[0.02]'}`}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click(); }}
            role="button"
            tabIndex={0}
          >
            <CloudUpload className="mb-2 text-slate-300" />
            <p className="text-sm font-medium">Drop photos here, or browse</p>
            <p className="mt-1 text-xs text-slate-400">{multiple ? 'You can add more than one photo.' : 'One photo at a time.'}</p>
            <input ref={inputRef} type="file" accept={accept} multiple={multiple} className="sr-only" onChange={(event) => { take(event.target.files); event.target.value = ''; }} />
          </div>
          {files.length > 0 && (
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {files.map((item) => {
                const preview = previewFor(item);
                return (
                  <li key={item.id} className="relative overflow-hidden rounded-xl border border-white/10 bg-black">
                    {preview ? <img src={preview} alt="" className="aspect-[4/3] w-full object-cover" /> : <div className="flex aspect-[4/3] items-center justify-center px-2 text-center text-xs text-slate-400">{item.file.name}</div>}
                    <button type="button" aria-label={`Remove ${item.file.name}`} className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1" onClick={() => onRemove(item.id)}>
                      <X size={12} />
                    </button>
                    {item.error && <p className="bg-red-950/80 px-2 py-1 text-[11px] text-red-200">{item.error}</p>}
                  </li>
                );
              })}
            </ul>
          )}
          {children}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-xl px-3 py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button type="button" onClick={onSubmit} disabled={submitting || files.length === 0 || files.every((item) => item.error)} className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReHubActivityHistory({
  loading,
  rows,
  empty,
}: {
  loading?: boolean;
  rows: { id: string; date: string; description: string; amount: string; positive?: boolean }[];
  empty: string;
}) {
  if (loading) {
    return <div className="space-y-2" aria-busy="true" aria-live="polite">{Array.from({ length: 6 }, (_, index) => <div key={index} className="h-12 animate-pulse rounded-xl bg-white/5" />)}</div>;
  }
  if (rows.length === 0) return <p className="rounded-2xl border border-dashed border-white/10 px-4 py-10 text-center text-sm text-slate-400">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3 font-medium">Date</th>
            <th className="px-4 py-3 font-medium">Description</th>
            <th className="px-4 py-3 text-right font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-white/5">
              <td className="whitespace-nowrap px-4 py-3 text-slate-400">{row.date}</td>
              <td className="px-4 py-3">{row.description}</td>
              <td className={`px-4 py-3 text-right font-semibold ${row.positive ? 'text-emerald-300' : 'text-red-300'}`}>{row.amount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
